import { apiRequest } from '../../../shared/api';
import {
  Finding,
  FindingDashboardStats,
  FindingStatus,
  FindingType,
  FindingSeverity,
  FindingSourceType,
  FindingCorrection,
  FindingRootCause,
  FindingVerification,
  FindingEffectiveness,
  FindingExtension,
  FindingAssignment,
  FindingRequirement,
  FindingEvidence,
  VerificationResult,
  EffectivenessResult,
} from './types';

export const findingService = {
  getDashboardStats: (organizationId: string = 'ORG-XYZ') =>
    apiRequest<FindingDashboardStats>(`/findings/analytics/dashboard?organizationId=${organizationId}`),

  getFindings: (params: {
    organizationId?: string;
    status?: FindingStatus;
    findingType?: FindingType;
    severity?: FindingSeverity;
    department?: string;
    sourceType?: FindingSourceType;
  } = {}) => {
    const query = new URLSearchParams();
    query.append('organizationId', params.organizationId || 'ORG-XYZ');
    if (params.status) query.append('status', params.status);
    if (params.findingType) query.append('findingType', params.findingType);
    if (params.severity) query.append('severity', params.severity);
    if (params.department) query.append('department', params.department);
    if (params.sourceType) query.append('sourceType', params.sourceType);
    return apiRequest<Finding[]>(`/findings?${query.toString()}`);
  },

  getFindingById: (id: string) => apiRequest<Finding>(`/findings/${id}`),

  createFinding: (data: {
    organizationId?: string;
    title: string;
    statement: string;
    description: string;
    findingType: FindingType;
    findingCategory: string;
    sourceType: FindingSourceType;
    sourceId?: string;
    sourceReference?: string;
    severity?: FindingSeverity;
    regulatoryLevel?: string;
    safetySignificance?: string;
    priority?: string;
    department: string;
    location?: string;
    process?: string;
    ownerId: string;
    ownerName: string;
    verifierId?: string;
    verifierName?: string;
    dueDate: string;
    requiresRootCause?: boolean;
    requiresCorrection?: boolean;
    requiresCapa?: boolean;
    requiresVerification?: boolean;
    requiresEffectiveness?: boolean;
    createdBy: string;
  }) => {
    return apiRequest<Finding>('/findings', {
      method: 'POST',
      body: JSON.stringify({
        organizationId: data.organizationId || 'ORG-XYZ',
        ...data,
      }),
    });
  },

  transitionStatus: (id: string, toStatus: FindingStatus, reason: string, actor: string = 'USER') => {
    return apiRequest<Finding>(`/findings/${id}/status`, {
      method: 'POST',
      body: JSON.stringify({ toStatus, reason, actor }),
    });
  },

  addAssignment: (
    id: string,
    data: {
      userId: string;
      userName: string;
      role: string;
      assignedBy: string;
    }
  ) => {
    return apiRequest<FindingAssignment>(`/findings/${id}/assignments`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  addRequirement: (
    id: string,
    data: {
      standard: string;
      clause: string;
      requirementText: string;
    }
  ) => {
    return apiRequest<FindingRequirement>(`/findings/${id}/requirements`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  addEvidence: (
    id: string,
    data: {
      title: string;
      fileName: string;
      fileSize: number;
      storageKey?: string;
      checksum?: string;
      sourceSystem?: string;
    }
  ) => {
    return apiRequest<FindingEvidence>(`/findings/${id}/evidence`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  addCorrection: (
    id: string,
    data: {
      description: string;
      containmentAction?: string;
      takenBy: string;
    }
  ) => {
    return apiRequest<FindingCorrection>(`/findings/${id}/corrections`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  addRootCause: (
    id: string,
    data: {
      method: string;
      rootCauseType: string;
      description: string;
      verifiedBy?: string;
    }
  ) => {
    return apiRequest<FindingRootCause>(`/findings/${id}/root-causes`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  addVerification: (
    id: string,
    data: {
      verifierId: string;
      verifierName: string;
      result: VerificationResult;
      notes: string;
    }
  ) => {
    return apiRequest<FindingVerification>(`/findings/${id}/verifications`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  addEffectiveness: (
    id: string,
    data: {
      evaluationDate: string;
      evaluatorId: string;
      evaluatorName: string;
      result: EffectivenessResult;
      evidenceNotes: string;
    }
  ) => {
    return apiRequest<FindingEffectiveness>(`/findings/${id}/effectiveness`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  requestExtension: (
    id: string,
    data: {
      newDueDate: string;
      reason: string;
      requestedBy: string;
    }
  ) => {
    return apiRequest<FindingExtension>(`/findings/${id}/extensions`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  approveExtension: (extensionId: string, approverId: string = 'QUALITY_DIRECTOR') => {
    return apiRequest<FindingExtension>(`/findings/extensions/${extensionId}/approve`, {
      method: 'POST',
      body: JSON.stringify({ approverId }),
    });
  },

  closeFinding: (id: string, closedBy: string = 'QUALITY_DIRECTOR', comment: string = 'Verified and completed.') => {
    return apiRequest<Finding>(`/findings/${id}/close`, {
      method: 'POST',
      body: JSON.stringify({ closedBy, comment }),
    });
  },

  reopenFinding: (id: string, reopenedBy: string = 'AUDITOR', reason: string) => {
    return apiRequest<Finding>(`/findings/${id}/reopen`, {
      method: 'POST',
      body: JSON.stringify({ reopenedBy, reason }),
    });
  },
};
